import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Palette, Plus, Trash2, Edit, ArrowLeft, Loader2, Star, Upload, Sparkles, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function EmptyIdentity() {
  return { name: "", description: "", tone: "", colors: [], elements: "", fixed_instructions: "", default: false, reference_urls: [], logo_url: "" };
}

export default function BrandIdentities() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const canManage = user?.role === "master" || user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brand-identities");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doSave = async () => {
    if (!editing.name.trim()) return toast.error("Nombre obligatorio");
    setSaving(true);
    try {
      const payload = { ...editing, colors: editing.colors.filter(Boolean) };
      if (editing.id) {
        await api.put(`/brand-identities/${editing.id}`, payload);
        toast.success("Identidad actualizada");
      } else {
        await api.post("/brand-identities", payload);
        toast.success("Identidad creada");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const doDelete = async (it) => {
    if (!window.confirm(`¿Eliminar identidad "${it.name}"?`)) return;
    try {
      await api.delete(`/brand-identities/${it.id}`);
      toast.success("Identidad eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const setColor = (i, v) => setEditing({ ...editing, colors: editing.colors.map((c, idx) => idx === i ? v : c) });
  const addColor = () => setEditing({ ...editing, colors: [...editing.colors, "#00A36C"] });
  const removeColor = (i) => setEditing({ ...editing, colors: editing.colors.filter((_, idx) => idx !== i) });

  // Fase 25 · Upload logo + referencias visuales
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/photo?module=brand_logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({ ...editing, logo_url: r.data.url });
      toast.success("Logo subido");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setUploadingLogo(false); }
  };
  const uploadReferences = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingRef(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        try {
          const r = await api.post("/upload/photo?module=brand_ref", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(r.data.url);
        } catch (_) { /* ignore individual */ }
      }
      setEditing({ ...editing, reference_urls: [...(editing.reference_urls || []), ...uploaded].slice(0, 8) });
      toast.success(`${uploaded.length} referencias subidas`);
    } finally { setUploadingRef(false); }
  };
  const removeReference = (idx) => setEditing({ ...editing, reference_urls: editing.reference_urls.filter((_, i) => i !== idx) });

  const doAnalyze = async () => {
    const urls = editing?.reference_urls || [];
    if (urls.length === 0 && !editing?.logo_url) {
      toast.error("Sube referencias o logo primero");
      return;
    }
    setAnalyzing(true);
    try {
      const r = await api.post("/brand-identities/analyze", {
        reference_urls: urls,
        logo_url: editing.logo_url || null,
      });
      setEditing({
        ...editing,
        description: r.data.description || editing.description,
        tone: r.data.tone || editing.tone,
        colors: (r.data.colors?.length ? r.data.colors : editing.colors),
        elements: r.data.elements || editing.elements,
      });
      toast.success("Identidad autocompletada por IA");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="brand-identities-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link to="/motor-creativo" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-brand-emerald-dark mb-2">
            <ArrowLeft className="h-3 w-3" /> Volver al Motor Creativo
          </Link>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span className="inline-flex h-10 w-10 rounded-lg items-center justify-center bg-brand-emerald text-white">
              <Palette className="h-5 w-5" strokeWidth={1.8} />
            </span>
            Identidades de marca
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            Define el estilo, tono y colores de cada identidad. Al generar, el motor compone el prompt automáticamente.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setEditing(EmptyIdentity())}
            data-testid="btn-new-identity"
            className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
          >
            <Plus className="h-4 w-4 mr-2" /> Nueva identidad
          </Button>
        )}
      </div>

      {loading ? (
        <div className="card-elevated p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500" data-testid="empty-identities">
          Aún no hay identidades. {canManage ? "Crea la primera con los colores y tono de tu marca." : "Solicita al Master o Admin que configure una."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.id} className="card-elevated p-5" data-testid={`identity-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif-display text-xl text-brand-ink">{it.name}</h3>
                    {it.default && <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30"><Star className="h-3 w-3 mr-1" /> por defecto</Badge>}
                  </div>
                  {it.description && <p className="text-sm text-stone-600 mt-1">{it.description}</p>}
                  {it.tone && <p className="text-xs text-stone-500 mt-1"><strong>Tono:</strong> {it.tone}</p>}
                </div>
                {canManage && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...it })} data-testid={`edit-${it.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => doDelete(it)} data-testid={`delete-${it.id}`} className="text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              {it.colors?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {it.colors.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border border-stone-200">
                      <span className="h-3 w-3 rounded-full border border-stone-200" style={{ backgroundColor: c }} />
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {it.elements && (
                <p className="text-xs text-stone-500 mt-2"><strong>Elementos:</strong> {it.elements}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="identity-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing?.id ? "Editar identidad" : "Nueva identidad de marca"}
            </DialogTitle>
            <DialogDescription>
              Estas instrucciones se aplicarán automáticamente cuando el usuario elija esta identidad al generar.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="identity-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Descripción</Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ej: elegante inmobiliaria, minimalista, foto de alta gama" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tono</Label>
                  <Input value={editing.tone || ""} onChange={(e) => setEditing({ ...editing, tone: e.target.value })} placeholder="cercano, profesional, aspiracional…" />
                </div>
                <div className="space-y-1.5 flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={editing.default} onChange={(e) => setEditing({ ...editing, default: e.target.checked })} data-testid="identity-default" />
                    <span>Marcar como identidad por defecto</span>
                  </label>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Colores</Label>
                <div className="space-y-1">
                  {editing.colors.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="color" value={c} onChange={(e) => setColor(i, e.target.value)} className="h-8 w-10 border rounded" />
                      <Input value={c} onChange={(e) => setColor(i, e.target.value)} className="max-w-[8rem] font-mono text-xs" />
                      <Button size="sm" variant="ghost" onClick={() => removeColor(i)} className="text-destructive">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {editing.colors.length < 8 && (
                    <Button size="sm" variant="outline" onClick={addColor} data-testid="add-color">
                      <Plus className="h-3 w-3 mr-1" /> Agregar color
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Elementos a incluir</Label>
                <Input value={editing.elements || ""} onChange={(e) => setEditing({ ...editing, elements: e.target.value })} placeholder="logo FStudy en esquina, marca de agua textual…" />
              </div>
              <div className="space-y-1.5">
                <Label>Instrucciones fijas</Label>
                <Textarea rows={3} value={editing.fixed_instructions || ""} onChange={(e) => setEditing({ ...editing, fixed_instructions: e.target.value })} placeholder="Estilo fotográfico profesional, luz natural, composición limpia sin distracciones…" />
              </div>

              {/* Fase 25 · Logo + Referencias visuales */}
              <div className="space-y-2 border-t pt-4">
                <div className="text-xs uppercase tracking-widest font-semibold text-stone-600">Logo de la marca</div>
                <div className="flex flex-col sm:flex-row gap-2 items-start">
                  <Input value={editing.logo_url || ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} placeholder="URL o subir archivo" data-testid="brand-logo-url" />
                  <label className="inline-flex items-center gap-1 rounded-md border border-input bg-white px-3 py-2 text-sm cursor-pointer hover:bg-stone-50">
                    {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="brand-logo-file" />
                    Subir
                  </label>
                </div>
                {editing.logo_url && (
                  <img src={editing.logo_url} alt="logo" className="h-16 w-16 object-contain border rounded bg-white" data-testid="brand-logo-preview" />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest font-semibold text-stone-600">Fotos de referencia ({(editing.reference_urls || []).length}/8)</div>
                  <label className="inline-flex items-center gap-1 text-sm cursor-pointer text-brand-emerald-dark hover:underline">
                    {uploadingRef ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                    <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadReferences(e.target.files)} data-testid="brand-ref-file" />
                    Añadir
                  </label>
                </div>
                {(editing.reference_urls || []).length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="brand-refs-preview">
                    {(editing.reference_urls || []).map((u, i) => (
                      <div key={i} className="relative w-16 h-16 rounded border overflow-hidden">
                        <img src={u} alt={`ref-${i}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeReference(i)} className="absolute top-0.5 right-0.5 bg-brand-ink/70 text-white rounded-full p-0.5" data-testid={`brand-ref-remove-${i}`}>
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={analyzing || ((editing.reference_urls || []).length === 0 && !editing.logo_url)}
                  onClick={doAnalyze}
                  data-testid="btn-brand-analyze"
                  className="border-brand-emerald text-brand-emerald-dark hover:bg-brand-emerald-soft"
                >
                  {analyzing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  Autocompletar con IA
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={doSave} disabled={saving} data-testid="btn-save-identity" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
